import ContactLink from "@/components/ContactLink";
import { Head } from "@inertiajs/react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import SectionHeading from "@/components/marketing/SectionHeading";

export default function Dpa() {
  return (
    <MarketingLayout>
      <Head title="Data Processing Agreement" />

      <section className="py-10 md:py-14">
        <div className="container">
          <div className="prose max-w-3xl mx-auto text-foreground">
            <SectionHeading
              as="h1"
              flourish="legal fine print"
              title="Data Processing Agreement"
              align="left"
              className="mb-4"
            />
            <p className="text-sm text-muted-foreground mb-10">
              Linked from the school onboarding flow. Schools are asked to
              review and accept this as part of approval.
            </p>

            <p className="mb-4 leading-relaxed">
              This Data Processing Agreement ("DPA") forms part of the Terms
              of Service between PagePledge Inc. ("PagePledge," "we") and
              the School ("you," "the Controller") when the School uses
              PagePledge to process Personal Information.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              1. Roles
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>The School is the Data Controller.</strong> The School
                determines what personal information to upload to PagePledge
                (student rosters), who has access (teachers, admins), and the
                purposes for which it is used (running a read-a-thon).
              </li>
              <li>
                <strong>PagePledge is the Data Processor.</strong> PagePledge
                processes personal information solely on the School's behalf
                and in accordance with these Terms.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              2. Scope of processing
            </h2>
            <p className="mb-4 leading-relaxed">
              PagePledge processes the following categories of personal
              information on behalf of the School:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Students:</strong> first name, shortened last name, a keyed
                surname fingerprint for matching repeat uploads, classroom
                assignment, grade, reading minutes logged, donations received
              </li>
              <li>
                <strong>Staff:</strong> name, email, role at the school
              </li>
              <li>
                <strong>Parents:</strong> email address (only after they activate
                a student), reading minutes they log
              </li>
              <li>
                <strong>Donors:</strong> name, email, donation amounts,
                optional messages (card details are entered directly into
                Square's payment form and never reach PagePledge)
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              PagePledge processes this information for the following purposes
              only:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Running the School's read-a-thon campaigns</li>
              <li>Generating take-home activation sheets</li>
              <li>Accepting and recording donations</li>
              <li>Displaying leaderboards and student pages</li>
              <li>
                Sending transactional and digest emails as described in the
                {" "}
                <a href="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </a>
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              3. PagePledge's obligations
            </h2>
            <p className="mb-4 leading-relaxed">PagePledge agrees to:</p>
            <ol className="list-decimal pl-6 space-y-2 mb-4">
              <li>
                Process personal information only on the School's documented
                instructions (which include the use of the Service as
                described in these Terms).
              </li>
              <li>
                Ensure that staff authorized to access personal information
                are bound by confidentiality.
              </li>
              <li>
                Implement appropriate technical and organizational security
                measures, including:
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>
                    Encryption of sensitive data at rest and in transit
                  </li>
                  <li>
                    Access controls limiting who can view production data
                  </li>
                  <li>
                    Records of sign-in events and platform-administrator
                    actions
                  </li>
                  <li>Regular backups</li>
                </ul>
              </li>
              <li>
                Assist the School in responding to requests from individuals
                to exercise their PIPEDA rights (access, correction, deletion,
                etc.).
              </li>
              <li>
                Notify the School without undue delay (within 72 hours where
                feasible) of any Personal Information Breach affecting the
                School's data.
              </li>
              <li>
                Delete or return all personal information at the School's
                request upon termination, subject to any legal retention
                requirements.
              </li>
              <li>
                Engage sub-processors only as disclosed in the{" "}
                <a href="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </a>{" "}
                and under equivalent contractual terms.
              </li>
            </ol>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              4. Sub-processors
            </h2>
            <p className="mb-4 leading-relaxed">
              PagePledge uses the following sub-processors to deliver the
              Service:
            </p>
            <div className="mb-4 overflow-x-auto">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead className="bg-secondary">
                  <tr>
                    <th className="px-4 py-2 text-left">Sub-processor</th>
                    <th className="px-4 py-2 text-left">Purpose</th>
                    <th className="px-4 py-2 text-left">Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      Square
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Payment processing
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Canada / United States
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      Amazon Simple Email Service (AWS)
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Transactional email
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Canada (Central)
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      DigitalOcean
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Hosting, database, and backups
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Toronto, Canada
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      AppSignal
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Error and performance monitoring, server logs
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      European Union
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      Google Fonts
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Web font delivery (receives visitors' IP address and
                      browser details)
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      United States
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mb-4 leading-relaxed">
              PagePledge will notify the School before adding or replacing a
              sub-processor. The School may object to a new sub-processor in
              good faith; if PagePledge cannot resolve the objection, the
              School may terminate the agreement.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              5. School's obligations
            </h2>
            <p className="mb-4 leading-relaxed">The School agrees to:</p>
            <ol className="list-decimal pl-6 space-y-2 mb-4">
              <li>
                Obtain any necessary parental consent or notification required
                by its local school board, provincial ministry, or other
                applicable regulations before uploading student information
                to PagePledge.
              </li>
              <li>
                Ensure uploaded data is accurate and limited to what is
                necessary for the campaign.
              </li>
              <li>
                Promptly update or delete information when students leave the
                school or when corrections are needed.
              </li>
              <li>
                Maintain the confidentiality of administrative credentials
                and promptly revoke access when staff leave.
              </li>
              <li>
                Use the Service in compliance with PIPEDA, Quebec Law 25 (if
                applicable), and any provincial education privacy rules.
              </li>
            </ol>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              6. Data transfers
            </h2>
            <p className="mb-4 leading-relaxed">
              PagePledge stores data primarily in Canada. Some sub-processors
              process data outside Canada, in the United States (Square,
              Google Fonts) or the European Union (AppSignal), under standard
              contractual safeguards. The School acknowledges and consents to
              this.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              7. Term and termination
            </h2>
            <p className="mb-4 leading-relaxed">
              This DPA remains in effect for as long as the School uses
              PagePledge. Upon termination, PagePledge will archive the
              School's data for 2 years, after which it will be permanently
              deleted, unless the School requests earlier deletion in writing.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              8. Liability
            </h2>
            <p className="mb-4 leading-relaxed">
              Liability under this DPA is subject to the limitations in the
              Terms of Service.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              9. Contact for privacy matters
            </h2>
            <p className="mb-4 leading-relaxed">
              For all data protection and privacy matters:
              <br />
              <strong>
                <ContactLink topic="privacy"
                  className="text-primary hover:underline"
                >
                  privacy@pagepledge.com
                </ContactLink>
              </strong>
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
